import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const programIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const programCheckedIcon = <CheckBoxIcon fontSize="small" />;

const AddTrainer = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [loading, setLoading] = useState(false);
  const [instituteNames, setInstituteNames] = useState([]);
  const [programs, setPrograms] = useState([]); // GetProgramsDropdown options for the chosen institute
  const location = useLocation();
  const { addTrainer, TrainerID } = location.state || "";
  const statuses = ["ACTIVE", "INACTIVE"];
  const [overAllLoading, setOverAllLoading] = useState();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const userRole = localStorage.getItem("role");
  // ===== validation ========
  const schema = Yup.object().shape({
    firstName: Yup.string()
      .required("Please enter the first name")
      .trim()
      .matches(/^[A-Za-z]+(?:[ .'-]+[A-Za-z]+)*\.?$/,"First name can only contain letters")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    lastName: Yup.string().required("Please enter the last name"),
    gender: Yup.string().required("Please select the gender"),
    dob: Yup.date()
      .required("Please enter the date of birth")
      .max(new Date(), "DOB cannot be in the future")
      .typeError("Please enter the date of birth"),
    contactNumber: Yup.string().required("Please enter the contact number"),
    email: Yup.string()
      .required("Please enter your Email Address")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid Email Address"
      ),
    // address: Yup.string().required("Please enter the address"),
    // city: Yup.string().required("Please enter the city"),
    // state: Yup.string().required("Please enter the state"),
    // pincode: Yup.string()
    //   .matches(/^[1-9][0-9]{5}$/, "Please enter a valid 6-digit pincode")
    //   .required("Please enter the pincode"),
    instituteName: Yup.string().required("Please enter the institute name"),
    assignedPrograms: Yup.array()
      .of(Yup.number())
      .min(1, "Please assign at least one program"),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      gender: "Male",
      dob: "",
      contactNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      status: "ACTIVE",
      instituteName: "",
      assignedPrograms: [],
    },
  });

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewTrainerByID", {
        user_id: TrainerID,
      });
      if (res?.data?.status === "success") {
        const d = res.data.data;
        reset({
          firstName: d.first_name,
          lastName: d.last_name,
          contactNumber: d.phone,
          email: d.email,
          instituteName: d.institute_id,
          status: d.status,
          address: d.address,
          city: d.city,
          state: d.state,
          pincode: d.pincode,
          gender: d.gender,
          dob: d.dob,
          // Edit-mode prefill: ViewTrainerByID returns assigned_programs as
          // [{ program_id, program_name }] — store just the ids for the multi-select.
          assignedPrograms: (d.assigned_programs || []).map((p) => p.program_id),
        });
        if (d.institute_id) getProgramsDropdown(d.institute_id);
      }
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      setOverAllLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setOverAllLoading(true);
    try {
      const payload = {
        first_name: data?.firstName,
        last_name: data?.lastName,
        gender: data?.gender,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        phone: data?.contactNumber,
        email: data?.email,
        address: data?.address,
        city: data?.city,
        state: data?.state,
        pincode: data?.pincode ? Number(data.pincode) : null,
        status: "ACTIVE",
        institute_id: +data?.instituteName,
        assigned_programs: data?.assignedPrograms ?? [],
      };
      const updatePayload = {
        user_id: TrainerID,
        first_name: data?.firstName,
        last_name: data?.lastName,
        gender: data?.gender,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        phone: data?.contactNumber,
        email: data?.email,
        address: data?.address,
        city: data?.city,
        state: data?.state,
        pincode: data?.pincode ? Number(data.pincode) : null,
        status: data?.status,
        institute_id: +data?.instituteName,
      };
      // Programs are a full-replace on the backend. Only send when the user actually
      // changed the selection — omitting leaves existing mappings untouched (handoff).
      if (dirtyFields?.assignedPrograms) {
        updatePayload.assigned_programs = data?.assignedPrograms ?? [];
      }
      if (TrainerID !== null) {
        const res = await api.post("UpdateTrainerByID", updatePayload);
        if (res?.data?.status === "success") {
          showSnackbar("success", res?.data?.message);
          navigate("/trainers");
        } else {
          showSnackbar("error", res?.data?.message);
        }
      } else {
        const res = await api.post("AddTrainer", payload);
        if (res?.data?.status === "success") {
          showSnackbar("success", res?.data?.message);
          navigate("/trainers");
        } else {
          showSnackbar("error", res?.data?.message);
        }
      }
    } catch (error) {
      console.log("Error :", error);
      // Surface backend messages (duplicate email/phone, cross-institute 403, etc.).
      showSnackbar("error", error?.response?.data?.message || "Something went wrong");
    } finally {
      setOverAllLoading(false);
    }
  };

  // Programs are institute-scoped — (re)fetch whenever an institute is chosen/available.
  const getProgramsDropdown = async (instituteId) => {
    if (!instituteId) {
      setPrograms([]);
      return;
    }
    try {
      const { data } = await api.post("GetProgramsDropdown", {
        institute_id: +instituteId,
      });
      setPrograms(data?.data ?? []);
    } catch (error) {
      console.error("Error fetching programs:", error);
      setPrograms([]);
    }
  };

  const instituteDropDown = async () => {
    try {
      const { data } = await api.post("ListInstituteNames");
      setInstituteNames(data?.data ?? []);
      // INSTITUTE_ADMIN is scoped to one institute — auto-select it and load its programs.
      // Use setValue (not reset) so this never clobbers fetchById's loaded values.
      if (userRole === "INSTITUTE_ADMIN") {
        const instId = data?.data?.[0]?.institute_id ?? "";
        setValue("instituteName", instId);
        getProgramsDropdown(instId);
      }
    } catch (error) {
      console.error("Error fetching institutes:", error);
    }
  };

  useEffect(() => {
    if (TrainerID !== null) {
      fetchById();
    }
    instituteDropDown();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Saving trainer..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* Form Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  borderRadius: "10px",
                }}
              >
                <SchoolIcon sx={{ color: "white", fontSize: "35px" }} />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {addTrainer === "true" ? "Add" : "Update"} {getLabel('TRAINER')}
                </Typography>
              </Grid>
            </Grid>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  p: 3,
                }}
              >
                {/* Basic Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <SchoolIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      First Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          placeholder="First Name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          placeholder="Last Name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Gender <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          placeholder="Select Gender"
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        >
                          <MenuItem value="default" disabled>
                            Select Gender
                          </MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Date of Birth <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="dob"
                      control={control}
                      rules={{ required: "Date of Birth is required" }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD-MM-YYYY"
                            value={
                              field.value
                                ? dayjs.isDayjs(field.value)
                                  ? field.value
                                  : dayjs(field.value)
                                : null
                            }
                            // ✅ Keep as dayjs during typing to prevent refresh
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dob,
                                helperText: errors.dob?.message,
                                placeholder: "DD-MM-YYYY",
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "white" },
                                    "&:hover fieldset": { borderColor: "#d1d1d1" },
                                    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                  },
                                  "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "white" },
                                  "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#d1d1d1" },
                                  "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#1c97e3" },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                </Grid>
                {addTrainer !== "true" && (
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    {/* Status */}
                    <Grid sx={{ width: "49%" }}>
                      <Typography>
                        Status <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            error={!!errors.status}
                            helperText={errors.status?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          >
                            {/* Placeholder */}
                            <MenuItem
                              value="default"
                              disabled
                              sx={{ color: "grey" }}
                            >
                              Select Status
                            </MenuItem>

                            {/* Options */}
                            {statuses.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Contact Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <MailOutlineIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Contact Number <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="contactNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          placeholder="Contact Number"
                          error={!!errors.contactNumber}
                          helperText={errors.contactNumber?.message}
                          onInput={(e) =>
                            (e.target.value = e.target.value.slice(0, 10))
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                              },
                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                              {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Email"
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Trainer Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <BusinessIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    {getLabel('INSTITUTE')} Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      {getLabel('INSTITUTE')} Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="instituteName"
                      control={control}
                      render={({ field }) => {
                        const currentValue =
                          instituteNames.find(
                            (inst) => inst.institute_id === field.value
                          ) || null;

                        return (
                          <Autocomplete
                            options={instituteNames}
                            getOptionLabel={(option) => option.name}
                            value={currentValue}
                            onChange={(_, value) => {
                              const id = value ? value.institute_id : "";
                              field.onChange(id);
                              // Institute changed → its programs differ; clear + reload.
                              setValue("assignedPrograms", []);
                              getProgramsDropdown(id);
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.institute_id === value.institute_id
                            }
                            readOnly={
                              userRole === "TRAINER" ||
                              userRole === "INSTITUTE_ADMIN"
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel('INSTITUTE')}`}
                                fullWidth
                                error={!!errors.instituteName}
                                helperText={errors.instituteName?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d1d1",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>

                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Assign {getLabel('PROGRAMS')} <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="assignedPrograms"
                      control={control}
                      render={({ field }) => {
                        const selected = programs.filter((p) =>
                          (field.value || []).includes(p.program_id)
                        );
                        return (
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={programs}
                            getOptionLabel={(option) => option.program_name || ""}
                            value={selected}
                            onChange={(_, value) =>
                              field.onChange(value.map((v) => v.program_id))
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.program_id === value.program_id
                            }
                            renderOption={(props, option, { selected: isSel }) => {
                              const { key, ...optionProps } = props;
                              return (
                                <li key={key} {...optionProps}>
                                  <Checkbox
                                    icon={programIcon}
                                    checkedIcon={programCheckedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={isSel}
                                  />
                                  {option.program_name}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel('PROGRAMS')}`}
                                fullWidth
                                error={!!errors.assignedPrograms}
                                helperText={errors.assignedPrograms?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d1d1",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>

                  {/* <Grid sx={{ width: "100%" }}>
                                <Typography>
                                  Role <span style={{ color: "red" }}>*</span>
                                </Typography>
                                <Controller
                                  name="role"
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      select
                                      fullWidth
                                      placeholder="Select Role"
                                      error={!!errors.role}
                                      helperText={errors.role?.message}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "white",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "#d1d1d1",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#1c97e3",
                                          },
                                        },
                                      }}
                                    >
                                      {roles.map((role) => (
                                        <MenuItem key={role} value={role}>
                                          {role}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                />
                              </Grid> */}
                </Grid>

                {/* Address Info */}
                {/* <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <LocationOnIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Address Information
                  </Typography>
                </Grid>
                <Typography>
                  Address <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Address"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "white",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d1d1d1",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#1c97e3",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      City <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="City"
                          error={!!errors.city}
                          helperText={errors.city?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      State <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="State"
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Pincode <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Pincode"
                          inputProps={{ maxLength: 6 }}
                          error={!!errors.pincode}
                          helperText={errors.pincode?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid> */}

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        window.history.back();
                      }
                    }}
                    onClick={() => window.history.back()}
                    sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      fontWeight: 600,
                    }}
                  >
                    {addTrainer === "true" ? `Add ${getLabel('TRAINER')}` : `Update ${getLabel('TRAINER')}`}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddTrainer;
